import { useQueryUser } from '@heidi/workspace/data';

import { useIsSessionMergeEnabled } from '../../flags';

/**
 * Whether the one-time Session Merge onboarding may be shown to this user: the
 * rollout flag is on but auto session merge is still off. Mirrors iOS
 * `SessionMergeAvailabilityClient.canPromptOnboarding`.
 *
 * This is one of only two flag consumers left (with the Settings surface).
 * The onboarding's "Try now" enables the same setting the flag hides in
 * Settings, so prompting flag-off users would promote a setting they cannot
 * see or manage — mirroring the equivalent Android change. All other
 * session-merge UI renders from server data unconditionally.
 *
 * Gated on the user query having succeeded — while it is still loading, the
 * setting is unknown, so we treat the user as not-yet-promptable (rather than
 * assuming off) to avoid flashing the prompt at someone who already has
 * auto-merge on. Only a loaded, missing/false setting is promptable.
 */
export function useCanPromptSessionMergeOnboarding(): boolean {
  const isSessionMergeEnabled = useIsSessionMergeEnabled();
  const { data: isAutoSessionMergeEnabled, isSuccess } = useQueryUser(
    (user) => user.session_settings?.enable_auto_session_merge
  );

  return isSessionMergeEnabled && isSuccess && !isAutoSessionMergeEnabled;
}
